import {Link} from 'react-router-dom'
import {faSpinnerThird} from '@fortawesome/pro-light-svg-icons'

import {twMerge} from '@sdox/utils'
import {getTrackingProps} from '@sdox/utils/dom'

import {FaIcon} from '../FaIcon'
import styles from './button.module.scss'

import type {ButtonBaseProps, ButtonProps} from './Button'
import type {LinkProps} from 'react-router-dom'
import type {ComponentPropsWithoutRef, MouseEventHandler} from 'react'

interface HrefButtonProps extends Omit<ComponentPropsWithoutRef<'a'>, 'href'>, ButtonBaseProps {
  href: string
  to?: never
  preventScrollReset?: never
}

interface RouterLinkButtonProps extends LinkProps, ButtonBaseProps {
  to: LinkProps['to']
  href?: never
}

export type LinkButtonProps = (HrefButtonProps | RouterLinkButtonProps) & {
  disabled?: boolean
}

const variantStyles: Record<NonNullable<ButtonProps['variant']>, string> = {
  'filled': styles.btnFilled,
  'filled-inversed': styles.btnFilledInversed,
  'filled-brand': styles.btnFilledBrand,
  'outline': styles.btnOutline,
  'outline-inversed': styles.btnOutlineInversed,
  'overlay': styles.btnOverlay,
  'overlay-inversed': styles.btnOverlayInversed,
  'overlay-brand': styles.btnOverlayBrand,
  'flat': styles.btnFlat,
}

const sizeStyles: Record<NonNullable<ButtonProps['size']>, string> = {
  none: '',
  xsmall: styles.btnExtraSmall,
  small: styles.btnSmall,
  medium: styles.btnMedium,
  large: styles.btnLarge,
}

export const LinkButton = (props: LinkButtonProps) => {
  const {
    variant = 'filled',
    size = 'large',
    leading,
    trailing,
    loading = false,
    className,
    onClick: onClickProp,
    contentClassName,
    children,
    mboAttrs,
    ...restProps
  } = props

  let {disabled} = restProps
  disabled ||= loading

  const mergedClassName = twMerge(
    'flex items-center',
    variantStyles[variant],
    sizeStyles[size],
    styles.btn,
    loading && styles.loading,
    className,
  )

  const onClick: MouseEventHandler<HTMLAnchorElement> = (event): void => {
    if (disabled) {
      event.preventDefault()
      event.stopPropagation()
    } else {
      onClickProp?.(event)
    }
  }

  const linkChildren = (
    <>
      {loading && (
        <span
          className={'absolute inset-0 flex items-center justify-center'}
        >
          <FaIcon
            className={twMerge(styles.spinner)}
            icon={faSpinnerThird}
          />
        </span>
      )}
      <span className={styles.btnContentWrapper}>
        {leading}
        {children && (
          <span className={twMerge(styles.content, contentClassName)}>
            {children}
          </span>
        )}
        {trailing}
      </span>
    </>
  )

  if (restProps.to !== undefined) {
    return (
      <Link
        onClick={onClick}
        className={mergedClassName}
        aria-disabled={disabled}
        children={linkChildren}
        {...(mboAttrs ? getTrackingProps(mboAttrs) : null)}
        {...restProps}
        to={restProps.to}
      />
    )
  }

  if (restProps.href !== undefined) {
    return (
      <a
        onClick={onClick}
        className={mergedClassName}
        aria-disabled={disabled}
        children={linkChildren}
        {...(mboAttrs ? getTrackingProps(mboAttrs) : null)}
        {...restProps}
        // open in new tab if origin differs
        target={restProps.href.toLowerCase().startsWith(window.location.origin.toLowerCase()) ? undefined : '_blank'}
      />
    )
  }

  return null
}
